/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { buildHashWithAuthMode, getLocationHash } from 'js/lib/authModeDeepLinkUtils';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import { Button } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { MOBILE_WEB_ENROLL_SLOT_CLASS_NAME } from 'bundles/composable-page-header/components/grid-items/MobileWebEnrollGridItem';
import { useMobileWebConversationalDiscoveryExperiment } from 'bundles/unified-common/contexts/MobileWebConversationalDiscoveryExperimentContext';

import _t from 'i18n!nls/composable-page-header-consumer';

const styles = {
  joinForFreeButton: css`
    margin-left: var(--cds-spacing-50);
  `,

  hiddenOnSmallScreens: css`
    /* Hide the button on the small screens where space is limited */
    @media (max-width: 435px) {
      display: none;
    }
  `,
  // Conversational Discovery on XDP for Mobile Web (MERCH-3206): the icon row applies its own
  // uniform gap, and on phone widths the XDP Enroll CTA portaled into the following nav enroll
  // slot leaves no room for this button.
  conversationalDiscoveryTreatment: css`
    margin-left: 0;

    @media (max-width: 450px) {
      &:has(~ .${MOBILE_WEB_ENROLL_SLOT_CLASS_NAME}:not(:empty)) {
        display: none;
      }
    }
  `,
};

const MobileJoinForFreeButton = () => {
  const track = useTracker();
  const location = useLocation();
  const navigate = useNavigate();
  const { mobileWebConversationalDiscoveryEnabled } = useMobileWebConversationalDiscoveryExperiment();

  const handleClick = () => {
    track('click_button', {
      button: { name: 'signup' },
      pageSection: { sectionName: 'sticky_page_navigation' },
    });

    const rawSearch =
      (location?.search && location.search.startsWith('?') ? location.search.slice(1) : location?.search) || '';
    const searchParams = new URLSearchParams(rawSearch);
    searchParams.delete('authMode');
    const searchString = searchParams.toString();

    navigate(
      {
        pathname: location?.pathname || '/',
        search: searchString ? `?${searchString}` : '',
        hash: buildHashWithAuthMode(getLocationHash(location), 'signup'),
      },
      { replace: true }
    );
  };

  return (
    <Button
      size="small"
      variant="secondary"
      onClick={handleClick}
      css={[
        styles.joinForFreeButton,
        !mobileWebConversationalDiscoveryEnabled && styles.hiddenOnSmallScreens,
        mobileWebConversationalDiscoveryEnabled && styles.conversationalDiscoveryTreatment,
      ]}
    >
      {_t('Join for Free')}
    </Button>
  );
};

export default MobileJoinForFreeButton;
